import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import TitleBlock from "@/components/TitleBlock";
import { fmtMoney, fmtNumber, fmtPercent, fmtMultiple } from "@/lib/format";
import {
  ArrowUpRight, ArrowDownRight, Minus, ArrowRight, CheckCircle2,
  AlertTriangle, TrendingUp, TrendingDown, Users, Megaphone, History, Activity, Target,
} from "lucide-react";

/**
 * DashboardPage — summary launchpad (Iter 26 rename).
 *
 * The three "modes of leadership work" each get a preview card here with a
 * deep-link to the full page: Rearview (last completed month), Console (this
 * month + next month decisions), Plan (the full year). Below sits an
 * Attention Needed strip that surfaces anything currently red across all
 * three surfaces so Joey knows what to click into.
 */

const SHORT_MONTH = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const monthLabel = (p) => {
  if (!p || p.length < 7) return p;
  const [y, m] = p.split("-");
  return `${SHORT_MONTH[+m - 1]} '${y.slice(2)}`;
};

/** Dashboard-style delta chip (matches Rearview/Console). */
function MomChip({ pct, invert = false, testid }) {
  if (pct === null || pct === undefined) return null;
  const isUp = pct > 0.5, isDown = pct < -0.5;
  const isGood = invert ? isDown : isUp;
  const isBad = invert ? isUp : isDown;
  const color = isGood ? "text-evergreen bg-evergreen/10"
    : isBad ? "text-red-700 bg-red-700/10"
    : "text-midnight bg-midnight/10 opacity-70";
  const Icon = pct > 0 ? ArrowUpRight : pct < 0 ? ArrowDownRight : Minus;
  return (
    <div className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold uppercase tracking-widest ${color}`}
         data-testid={testid}>
      <Icon className="w-3 h-3" />
      {pct > 0 ? "+" : ""}{pct.toFixed(1)}%
    </div>
  );
}

/**
 * SectionCard — one preview card per page.  Header · big value · sub-metrics
 * · CTA button to the deep-link.
 */
function SectionCard({ eyebrow, title, subtitle, headline, sub, extras, cta, to, icon: Icon, borderColor, testid }) {
  return (
    <div className={`bg-warm-tint border-t-4 ${borderColor} p-6 flex flex-col`}
         data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4 text-cobalt" />
        <div className="text-[10px] uppercase tracking-[0.22em] text-cobalt font-bold">{eyebrow}</div>
      </div>
      <div className="text-xl font-bold text-navy">{title}</div>
      {subtitle && <div className="text-xs text-midnight opacity-60 mb-3">{subtitle}</div>}
      <div className="text-3xl font-bold text-navy mt-2" data-testid={`${testid}-headline`}>{headline}</div>
      {sub && <div className="text-sm text-midnight opacity-80 mt-1">{sub}</div>}
      {extras && <div className="mt-4 grid grid-cols-2 gap-3 text-xs">{extras}</div>}
      <div className="mt-auto pt-5">
        <Link to={to} data-testid={`${testid}-cta`}
              className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-cobalt hover:text-navy">
          {cta} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

function Metric({ label, value, chip }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest opacity-60">{label}</div>
      <div className="font-bold text-navy">{value}</div>
      {chip}
    </div>
  );
}

export default function DashboardPage() {
  const [pacing, setPacing] = useState(null);
  const [platMonthly, setPlatMonthly] = useState(null);
  const [salesCtx, setSalesCtx] = useState(null);
  const [avt, setAvt] = useState(null);
  const [scaling, setScaling] = useState(null);
  const [recon, setRecon] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/analytics/pacing").catch(() => ({ data: null })),
      api.get("/analytics/platform-monthly?months=12").catch(() => ({ data: null })),
      api.get("/analytics/sales-context-monthly?months=12").catch(() => ({ data: null })),
      api.get(`/targets/actual-vs-target?year=${new Date().getFullYear()}`).catch(() => ({ data: null })),
      api.get("/analytics/scaling-recommendation?days=30").catch(() => ({ data: null })),
      api.get("/analytics/reconciliation-timeline?days=365").catch(() => ({ data: null })),
    ]).then(([p, pm, sc, a, sr, rc]) => {
      setPacing(p.data); setPlatMonthly(pm.data); setSalesCtx(sc.data);
      setAvt(a.data); setScaling(sr.data); setRecon(rc.data);
    });
  }, []);

  // ── Rearview card data: latest completed month + top mover
  const rvLatestPeriod = platMonthly?.latest_period;
  const rvGoogleRoas = platMonthly?.google?.roas;
  const rvMetaRoas = platMonthly?.meta?.roas;
  const rvRevenue = platMonthly?.google?.spend?.value != null && platMonthly?.google?.roas?.value != null
    ? platMonthly.google.spend.value * platMonthly.google.roas.value
      + (platMonthly.meta?.spend?.value || 0) * (platMonthly.meta?.roas?.value || 0)
    : null;
  const biggestMover = salesCtx?.biggest_mover;

  // ── Console card data: MTD pacing
  const cnOnTrack = pacing?.projected?.on_track;
  const cnDaysRem = pacing?.days_remaining;
  const cnMtdRev = pacing?.mtd?.revenue;
  const cnMtdSales = pacing?.mtd?.closed_deals;
  const cnProjected = pacing?.projected?.revenue;

  // ── Plan card data: year-end projection vs original
  const plTotals = avt?.totals;
  const plDeltaVsOriginal = plTotals?.revenue_delta_vs_plan;
  const plYearEnd = plTotals?.adjusted_revenue_year;
  const plPctOfPlan = plTotals?.revenue_pct_of_plan;

  // ── Attention Needed: paused platforms, reconciliation outliers, sync errors
  const pausedCount = (scaling?.platforms || []).filter((p) => p.verdict?.key === "pause").length;
  const holdCount = (scaling?.platforms || []).filter((p) => p.verdict?.key === "hold").length;
  const threshold = recon?.mismatch_threshold_pct || 2;
  const flatten = (arr, keys) => (arr || []).flatMap((row) => keys.map((k) => row[k]).filter((v) => v != null));
  const hsOutliers = flatten(recon?.hubspot_vs_sheet, ["revenue_delta_pct", "sales_delta_pct"])
    .filter((v) => Math.abs(v) > threshold).length;
  const rtOutliers = flatten(recon?.railtown_vs_bhag, ["google_delta_pct", "meta_delta_pct"])
    .filter((v) => Math.abs(v) > threshold).length;
  const totalReconOutliers = hsOutliers + rtOutliers;

  const attention = [
    {
      label: pausedCount > 0
        ? `${pausedCount} platform${pausedCount > 1 ? "s" : ""} flagged Pause / Reallocate`
        : holdCount > 0
          ? `${holdCount} platform${holdCount > 1 ? "s" : ""} on Hold — watch ROAS`
          : "All paid channels healthy",
      severity: pausedCount > 0 ? "red" : holdCount > 0 ? "yellow" : "green",
      to: "/console",
      cta: "Open Console",
      testid: "attention-paid-channels",
    },
    {
      label: totalReconOutliers > 0
        ? `${totalReconOutliers} reconciliation outlier${totalReconOutliers > 1 ? "s" : ""} beyond ±${threshold}%`
        : "Sources aligned within tolerance",
      severity: totalReconOutliers > 3 ? "red" : totalReconOutliers > 0 ? "yellow" : "green",
      to: "/plan",
      cta: "Open Plan",
      testid: "attention-reconcile",
    },
    {
      label: cnOnTrack ? `Pacing ahead this month — ${fmtMoney(cnProjected)} projected`
                       : `Behind pace — ${fmtMoney(pacing?.gap?.revenue)} to close`,
      severity: cnOnTrack ? "green" : "red",
      to: "/console",
      cta: "Open Console",
      testid: "attention-pacing",
    },
  ];

  if (!pacing || !avt) return <div className="p-10 text-navy">Loading dashboard…</div>;

  return (
    <div data-testid="dashboard-page">
      <TitleBlock
        eyebrow="At a glance"
        title="Marketing Intelligence"
        subtitle="One-screen summary of Rearview (last month), Console (this month), and Plan (the year). Deep-link into any section for the full view."
      />

      <div className="p-6 md:p-8 space-y-8">

        {/* ── Attention Needed strip ── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="attention-strip">
          {attention.map((a, i) => {
            const cls = a.severity === "red" ? "border-red-700 bg-red-700/5"
              : a.severity === "yellow" ? "border-orange-700 bg-orange-700/5"
              : "border-evergreen bg-evergreen/5";
            const Icon = a.severity === "green" ? CheckCircle2 : AlertTriangle;
            const iconCls = a.severity === "red" ? "text-red-700"
              : a.severity === "yellow" ? "text-orange-700"
              : "text-evergreen";
            return (
              <Link key={i} to={a.to} data-testid={a.testid}
                    className={`p-4 border-l-4 ${cls} flex items-start gap-3 hover:bg-warm transition-colors`}>
                <Icon className={`w-5 h-5 shrink-0 mt-0.5 ${iconCls}`} />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-navy">{a.label}</div>
                  <div className="text-[10px] uppercase tracking-widest text-cobalt mt-1 inline-flex items-center gap-1">
                    {a.cta} <ArrowRight className="w-3 h-3" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* ── Three preview cards ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Rearview preview */}
          <SectionCard
            testid="section-rearview"
            eyebrow="Rearview"
            title="Last completed month"
            subtitle={monthLabel(rvLatestPeriod)}
            headline={rvRevenue != null ? fmtMoney(rvRevenue) : "—"}
            sub="Attributable ad revenue · Google + Meta"
            icon={History}
            borderColor="border-cobalt"
            to="/rearview"
            cta="Open Rearview"
            extras={<>
              <Metric label="Google ROAS"
                      value={rvGoogleRoas?.value != null ? fmtMultiple(rvGoogleRoas.value) : "—"}
                      chip={<MomChip pct={rvGoogleRoas?.delta_pct_mom} testid="rv-google-mom" />} />
              <Metric label="Meta ROAS"
                      value={rvMetaRoas?.value != null ? fmtMultiple(rvMetaRoas.value) : "—"}
                      chip={<MomChip pct={rvMetaRoas?.delta_pct_mom} testid="rv-meta-mom" />} />
              {biggestMover && (
                <div className="col-span-2 mt-1 p-2 bg-warm border-l-2 border-cobalt/60">
                  <div className="text-[10px] uppercase tracking-widest opacity-60">Biggest MoM mover</div>
                  <div className="text-navy font-semibold text-xs">
                    {biggestMover.label}{" "}
                    <span className={biggestMover.mom_pct >= 0 ? "text-evergreen" : "text-red-700"}>
                      {biggestMover.mom_pct >= 0 ? "+" : ""}{biggestMover.mom_pct?.toFixed(1)}%
                    </span>
                    <span className="opacity-60"> · owned by {biggestMover.owner}</span>
                  </div>
                </div>
              )}
            </>}
          />

          {/* Console preview */}
          <SectionCard
            testid="section-console"
            eyebrow="Console"
            title="This month · MTD"
            subtitle={`Day ${pacing.days_elapsed}/${pacing.days_in_month} · Slack authoritative`}
            headline={fmtMoney(cnMtdRev)}
            sub={cnOnTrack
              ? `Pacing ahead · projected ${fmtMoney(cnProjected)}`
              : `Behind pace · needs ${fmtMoney(pacing?.gap?.daily_revenue_needed)}/day for ${cnDaysRem} more days`}
            icon={Activity}
            borderColor={cnOnTrack ? "border-evergreen" : "border-red-700"}
            to="/console"
            cta="Open Console"
            extras={<>
              <Metric label="Sales MTD" value={fmtNumber(cnMtdSales, 0)} />
              <Metric label="Calls booked" value={fmtNumber(pacing?.funnel?.booked, 0)} />
              <Metric label="Held" value={fmtNumber(pacing?.funnel?.held, 0)} />
              <Metric label="Days remaining" value={fmtNumber(cnDaysRem, 0)} />
            </>}
          />

          {/* Plan preview */}
          <SectionCard
            testid="section-plan"
            eyebrow="Plan"
            title="The year"
            subtitle="Adjusted forecast · past actuals + remaining BHAG"
            headline={fmtMoney(plYearEnd)}
            sub={<span>
              {plDeltaVsOriginal >= 0 ? "+" : ""}{fmtMoney(plDeltaVsOriginal)} vs original plan ·{" "}
              <span className={plPctOfPlan >= 100 ? "text-evergreen font-bold" : "text-red-700 font-bold"}>
                {fmtPercent((plPctOfPlan || 0) / 100)}
              </span> of plan
            </span>}
            icon={Target}
            borderColor="border-navy"
            to="/plan"
            cta="Open Plan"
            extras={<>
              <Metric label="Original plan" value={fmtMoney(plTotals?.plan_revenue_year)} />
              <Metric label="Actual YTD" value={fmtMoney(plTotals?.actual_revenue_ytd)} />
              <Metric label="Sales YTD" value={fmtNumber(plTotals?.actual_sales_ytd, 0)} />
              <Metric label="Sales plan" value={fmtNumber(plTotals?.plan_sales_year, 0)} />
            </>}
          />
        </div>

        {/* ── Ownership context (from Console) ── */}
        {biggestMover && (
          <div className="bg-warm border-l-4 border-cobalt p-4 flex items-center gap-4"
               data-testid="dashboard-ownership">
            <div className="text-[10px] uppercase tracking-[0.22em] text-cobalt font-bold shrink-0">
              Ownership signal
            </div>
            <div className="flex-1 text-sm text-midnight">
              <strong className="text-navy">{biggestMover.label}</strong> {biggestMover.mom_pct >= 0 ? "up" : "down"}{" "}
              <span className={biggestMover.mom_pct >= 0 ? "text-evergreen font-bold" : "text-red-700 font-bold"}>
                {Math.abs(biggestMover.mom_pct || 0).toFixed(1)}%
              </span>{" "}
              MoM · sits with{" "}
              <span className="inline-flex items-center gap-1 font-semibold text-navy">
                {biggestMover.owner === "marketing" ? <Megaphone className="w-3.5 h-3.5" /> : <Users className="w-3.5 h-3.5" />}
                {biggestMover.owner}
              </span>.
              {biggestMover.yoy_available && biggestMover.yoy_pct != null && (
                <span className="opacity-70"> YoY {biggestMover.yoy_pct >= 0 ? "+" : ""}{biggestMover.yoy_pct.toFixed(1)}%.</span>
              )}
            </div>
            <Link to="/console"
                  className="text-[10px] uppercase tracking-widest text-cobalt hover:text-navy inline-flex items-center gap-1">
              Console <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
